/**
 * Soft≠Live Page172 — Fleet Groups residual liquid (page140/70 + status SoT).
 * Mount: /fleet/groups
 * Buses:
 *   fleet groups status (always-on Live posture)
 *   devices register · profile membership write
 * Soft residual deleted:
 *  - sandpit Groups body without rail / Live KPI / posture SoT
 *  - Register invent when seat gated
 *  - toast invent membership without write
 *  - ghost members when register fails
 *  - draft-only invent that pretends server has the group before Save
 */
import { useEffect, useMemo, useState } from "react";
import { html, M, Ico, Card, Field, Input, Alert, Ticker, SPRING, go, KV, canonicalGroupKey, sameGroupKey } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";

/** Soft operator errors — never paint wire paths. */
export function softFleetGroupsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|insufficient|not_authorized|device not in|cannot change|operation denied/.test(low)) {
    return "Your account cannot change this group. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That unit or group is not available.";
  }
  if (/\b400\b|invalid|required/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline|revoked/.test(low)) {
    return "This unit is not open to commands right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/devices\/|\/fleet\/|\/group|http|GET |POST |PATCH |PUT |status_path/.test(low)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}

/** Kind tone table local to groups desk — no estate cycle import. */
const KINDS = {
  camera:  { t: "Camera",       glyph: "camera", tone: "#3DE1C8" },
  contact: { t: "Door contact", glyph: "door",   tone: "#7FD1FF" },
  siren:   { t: "Siren",        glyph: "siren",  tone: "#FF9B5C" },
  gateway: { t: "Mast / gateway", glyph: "mast", tone: "#A78BFA" },
  sensor:  { t: "Sensor",       glyph: "sensor", tone: "#9BE37D" },
  lidar:   { t: "Lidar",        glyph: "lidar",  tone: "#FFD166" },
  node:    { t: "Node",         glyph: "chip",   tone: "#8CA2A6" },
};
const kindOf = (k) => KINDS[k] || KINDS.node;

/** Pure: glyph family from Live list columns — never invent sensor kind Soft. */
function guessKind(row) {
  const blob = [
    row && row.display_label, row && row.map_label, row && row.hw_code_resolved,
    row && row.product_hw_code, row && row.board_profile, row && row.chip_target,
  ].filter(Boolean).join(" ").toLowerCase();
  if (/\b(cam|camera|snap|vision|lidar)\b/.test(blob)) return /lidar/.test(blob) ? "lidar" : "camera";
  if (/\b(siren|strobe|horn|alarm)\b/.test(blob)) return "siren";
  if (/\b(door|contact|reed|magnet)\b/.test(blob)) return "contact";
  if (/\b(mast|gateway|bridge|hub)\b/.test(blob)) return "gateway";
  if (/\b(sensor|leak|smoke|pir|temp)\b/.test(blob)) return "sensor";
  return "node";
}

/** Pure: flatten devices register into group desk row. */
export function fleetGroupDeviceOf(row) {
  if (!row || typeof row !== "object") return null;
  const id = String(row.device_id || "").trim();
  if (!id) return null;
  const label = String(row.display_label || row.map_label || "").trim() || id;
  return {
    id,
    label,
    kind: guessKind(row),
    zone: String(row.zone || "").trim(),
    group: String(row.notification_group || "").trim(),
  };
}

/** Pure: device rows from devices pack (Live only). */
export function fleetGroupRegisterOf(devicesPack) {
  const items = (devicesPack && (devicesPack.items || devicesPack.devices)) || [];
  if (!Array.isArray(items)) return [];
  return items.map(fleetGroupDeviceOf).filter(Boolean);
}

/** Pure: KPI strip for Live group names + membership. */
export function fleetGroupKpisOf(devs) {
  const rows = Array.isArray(devs) ? devs : [];
  const named = rows.filter((d) => d && d.group).length;
  const groups = new Set(rows.map((d) => canonicalGroupKey(d && d.group)).filter(Boolean));
  return {
    devices: rows.length,
    inGroup: named,
    unassigned: Math.max(0, rows.length - named),
    groups: groups.size,
  };
}

const Chip = ({ kind }) => {
  const k = kindOf(kind);
  return html`<span className="kchip" data-k=${kind} style=${{ "--kc": k.tone }} title=${k.t}>
    <${Ico} d=${k.glyph} w=${13}/></span>`;
};

export function FleetGroupsDesk({ toast, onOpenZones }) {
  const posture = useApi(path("get__fleet_groups_status"));
  const post = posture.data || {};
  /* Fail-closed: never invent open seats while status is missing. */
  const registerOk = posture.loading ? false : post.register_allowed === true;
  /* Fail-closed: never invent write from a missing alternate field. */
  const writeOk = posture.loading ? false : (
    post.membership_write_allowed === true || post.write_allowed === true
  );
  const postureLabel = String(post.posture_label || "").trim()
    || (registerOk ? "Groups register open" : "Groups closed for this seat");

  const devices = useApi(registerOk ? path("get__devices") : "");
  const register = useMemo(() => fleetGroupRegisterOf(devices.data), [devices.data]);
  const [devs, setDevs] = useState([]);
  const [pick, setPick] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftNames, setDraftNames] = useState([]);
  const [newName, setNewName] = useState("");
  const save_ = useAction();
  const envelope = save_.envelope;

  useEffect(() => {
    if (dirty) return;
    setDevs(register.map((d) => ({ ...d })));
    if (!pick && register.length) {
      const first = register.find((d) => d.group)?.group || "";
      setPick(canonicalGroupKey(first) || first);
    }
  }, [register, dirty, pick]);

  const names = useMemo(() => {
    const seen = new Set();
    const fromDevs = [];
    for (const d of devs) {
      const k = canonicalGroupKey(d && d.group);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      fromDevs.push(k);
    }
    const extra = draftNames
      .map((n) => canonicalGroupKey(n))
      .filter((k) => k && !seen.has(k));
    return [...fromDevs, ...extra];
  }, [devs, draftNames]);

  const members = pick ? devs.filter((d) => sameGroupKey(d.group, pick)) : [];
  const outside = pick ? devs.filter((d) => !sameGroupKey(d.group, pick)) : [];
  const sirens = members.filter((d) => d.kind === "siren").length;
  const kpis = useMemo(() => fleetGroupKpisOf(devs), [devs]);
  const err = devices.error;

  const move = (id, group) => {
    if (!writeOk) {
      toast && toast("Membership changes closed for this seat", "warn");
      return;
    }
    const next = group === "Unassigned" || !group ? "" : canonicalGroupKey(group);
    setDevs((v) => v.map((d) => (d.id === id ? { ...d, group: next } : d)));
    setDirty(true);
  };

  const startGroup = () => {
    if (!writeOk) {
      toast && toast("Membership changes closed for this seat", "warn");
      return;
    }
    const n = String(newName || "").trim();
    if (!n) {
      toast && toast("Enter a group name first", "warn");
      return;
    }
    if (n === "Unassigned") {
      toast && toast("Unassigned is reserved for remove", "warn");
      return;
    }
    const key = canonicalGroupKey(n);
    if (names.some((x) => sameGroupKey(x, key))) {
      setPick(key);
      setNewName("");
      toast && toast("Group already listed · selected", "go");
      return;
    }
    setDraftNames((v) => [...v, key]);
    setPick(key);
    setNewName("");
    toast && toast("Group named · move units in, then save membership", "go");
  };

  const saveMembership = async () => {
    if (saving || !writeOk) return;
    const byId = Object.fromEntries(register.map((d) => [d.id, d]));
    const changed = devs.filter((d) => {
      const o = byId[d.id];
      if (!o) return false;
      const next = d.group === "Unassigned" ? "" : canonicalGroupKey(d.group);
      return canonicalGroupKey(o.group) !== next;
    });
    if (!changed.length) {
      setDirty(false);
      toast && toast("Nothing to write", "warn");
      return;
    }
    setSaving(true);
    let ok = 0;
    try {
      for (const d of changed) {
        const group = d.group === "Unassigned" || !d.group ? "" : canonicalGroupKey(d.group);
        const out = await save_.run(path("patch__devices_p_profile", { pathParams: [d.id] }), {
          method: "PATCH",
          body: { notification_group: group },
        });
        if (!out) {
          toast && toast(softFleetGroupsErrorOf(save_.error || ("refused " + d.id)), "bad");
          return;
        }
        ok += 1;
      }
      setDirty(false);
      setDraftNames((v) => v.filter((n) => devs.some((d) => sameGroupKey(d.group, n))));
      toast && toast(ok + " membership change(s) saved", "go");
      devices.reload && devices.reload();
    } finally {
      setSaving(false);
    }
  };

  const reRead = () => {
    posture.reload && posture.reload();
    devices.reload && devices.reload();
    if (!dirty) {
      setDraftNames([]);
    }
    toast && toast("Groups status refreshed", "go");
  };

  const discard = () => {
    setDevs(register.map((d) => ({ ...d })));
    setDirty(false);
    setDraftNames((v) => v.filter((n) => register.some((d) => sameGroupKey(d.group, n))));
    toast && toast("Draft discarded", "warn");
  };

  if (!posture.loading && post.enabled !== true) {
    return html`<div className="fleet-groups-desk fleet-groups-desk--liquid"
      data-cmc-page="page140-groups" data-cmc-legacy="page70-groups"
      data-cmc-residual="page172-groups">
      <div className="fleet-groups-desk__rail">
        <div>
          <p className="eyebrow">Fleet · Groups</p>
          <h2 className="fleet-groups-desk__title">Seat closed</h2>
          <p className="note">Your seat cannot open groups on this grant.</p>
        </div>
        <div className="btn-row fleet-groups-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Refresh</button>
        </div>
      </div>
      <${Card} title="Groups posture" meta="closed">
        <p className="note">${postureLabel || "Groups closed for this seat"} · role ${post.principal_role || "—"}</p>
      <//>
    </div>`;
  }

  return html`<div className="fleet-groups-desk fleet-groups-desk--liquid"
    data-cmc-page="page140-groups" data-cmc-legacy="page70-groups"
    data-cmc-residual="page172-groups">
    <div className="fleet-groups-desk__rail">
      <div>
        <p className="eyebrow">Fleet · Groups</p>
        <h2 className="fleet-groups-desk__title">Membership</h2>
        <p className="note">
          Name a group, move units in or out, then save. Draft names are local until save.
        </p>
      </div>
      <div className="btn-row fleet-groups-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${posture.loading || devices.loading}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => onOpenZones ? onOpenZones() : go("/places/zones")}>Zones</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/fleet/health")}>Health</button>
      </div>
    </div>

    <${Alert} tone="info" title="Groups on this account"
      say="A group is only a label on each unit. Name a group, move units, then save. Draft names do not exist on the server until you save."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Card} title="Groups posture" meta="seat">
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="register" v=${registerOk ? "open" : "closed"}/>
        <${KV} k="membership write" v=${writeOk ? "open" : "closed"}/>
        <${KV} k="draft as server" v=${"refused"}/>
      </div>
      ${postureLabel ? html`<p className="note" style=${{ marginTop: 8 }}>${postureLabel}</p>` : null}
    <//>

    ${err ? html`
      <${Alert} tone="warn" title="Device register refused or partial"
        say=${softFleetGroupsErrorOf(err)}/>`
    : null}

    <div className="fleet-groups-desk__kpis" role="group" aria-label="Groups KPIs">
      ${[
        ["Devices", kpis.devices, kpis.devices ? "1" : "0", ""],
        ["In a group", kpis.inGroup, kpis.inGroup ? "1" : "0", ""],
        ["Unassigned", kpis.unassigned, kpis.unassigned ? "1" : "0", "off"],
        ["Named groups", kpis.groups, kpis.groups ? "1" : "0", ""],
      ].map(([k, v, on, tone]) => html`
        <div key=${k} className="fleet-groups-desk__kpi" data-on=${on} data-tone=${tone}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>Device register</em>
        </div>`)}
    </div>

    <${Card} title="Groups" meta="label on each unit"
        sub="A group decides who is woken together. There is no separate group table — only labels on devices.">
      <div className="gpick">
        ${!names.length ? html`<p className="note" style=${{ margin: "0 0 10px", width: "100%" }}>
          No groups yet — name one below, then move units in.</p>` : null}
        ${names.map((n) => {
          const m = devs.filter((d) => sameGroupKey(d.group, n));
          const isDraft = !register.some((d) => sameGroupKey(d.group, n));
          return html`
            <button key=${n} type="button" className="gpick__b" data-on=${sameGroupKey(pick, n) ? "1" : "0"}
              onClick=${() => setPick(canonicalGroupKey(n) || n)}>
              ${sameGroupKey(pick, n) && html`<${M.span} layoutId="gpick" className="gpick__pill" transition=${SPRING}/>`}
              <span className="gpick__t">
                <b>${n}${isDraft ? " · draft" : ""}</b>
                <em>${m.length} device${m.length === 1 ? "" : "s"}${isDraft ? " · not saved yet" : ""}</em>
              </span>
            <//>`;
        })}
      </div>
      <div className="btn-row" style=${{ marginTop: 14, alignItems: "flex-end", flexWrap: "wrap", gap: 10 }}>
        <${Field} label="New group name" hint="Draft until you save membership">
          <${Input} icon="grid" value=${newName} placeholder="e.g. Gate-West"
            onChange=${(e) => setNewName(e.target.value)}
            onKeyDown=${(e) => { if (e.key === "Enter") { e.preventDefault(); startGroup(); } }}
            act="Name" onAct=${startGroup}/>
        <//>
        <button className="btn btn--go btn--sm" type="button" disabled=${!writeOk}
          onClick=${startGroup}>
          <${Ico} d="plus" w=${13}/> Name group</button>
      </div>
    <//>

    <div className="grid g2">
      <${Card} title=${pick || "No group selected"} meta=${pick
        ? (members.length + " members · " + (sirens || "no") + " siren" + (sirens === 1 ? "" : "s"))
        : "name a group first"}>
        ${!pick ? html`<p className="note">Name a group above, then click devices on the right to move them in.</p>` : html`
          <button className="btn btn--ghost btn--sm" type="button" style=${{ marginBottom: 12 }}
          onClick=${() => go("/machine/groups/" + encodeURIComponent(pick))}>
            <${Ico} d="grid" w=${13}/>Open the group page</button>
          ${sirens === 0 && html`
            <${Alert} tone="warn" icon="alert" title="Nothing in this group can sound"
              say="A trigger here notifies the group's recipients and stays silent. Add a siren if this group must be heard."/>`}
          <div className="mlist">
            ${members.length ? members.map((d) => html`
              <div key=${d.id} className="mrow">
                <${Chip} kind=${d.kind}/>
                <button className="mrow__t mrow__t--go" type="button" onClick=${() => go("/machine/" + d.id)}
                    title="Open the full record">
                  <b>${d.label}</b><em>${d.zone || "—"} · ${d.id}</em></button>
                <button className="mrow__x" type="button" title="Remove from group" disabled=${!writeOk}
                  onClick=${() => move(d.id, "Unassigned")}><${Ico} d="close" w=${12}/></button>
              </div>`) : html`<p className="note">No members yet — move devices in from the right, then Save.</p>`}
          </div>`}
      <//>

      <${Card} title="Everything else on this site" meta=${pick ? "click to move it in" : "select a group first"}>
        ${!pick ? html`<p className="note">Select or name a group first.</p>` : html`
          <div className="mlist">
            ${outside.map((d) => html`
              <button key=${d.id} type="button" className="mrow mrow--add"
                disabled=${!writeOk}
                onClick=${() => move(d.id, pick)}>
                <${Chip} kind=${d.kind}/>
                <span className="mrow__t"><b>${d.label}</b><em>${d.zone || "—"} · ${d.group || "unassigned"}</em></span>
                <${Ico} d="plus" w=${13}/>
              <//>`)}
            ${!outside.length ? html`<p className="note">Every visible device is already in this group.</p>` : null}
          </div>`}
      <//>
    </div>

    <${Card} title="What changes when you save" meta="Server write"
        sub="One save per moved unit. Stops at the first refusal — no half-applied theatre.">
      <div className="btn-row fleet-groups-desk__acts">
        <button className="btn btn--go btn--sm" type="button" disabled=${!writeOk || !dirty || saving} onClick=${saveMembership}>
          ${saving ? "Writing…" : "Save membership"}</button>
        <button className="btn btn--ghost btn--sm" type="button" disabled=${!dirty || saving} onClick=${discard}>
          Discard draft</button>
        ${dirty && html`<span className="note" style=${{ margin: 0 }}>Unsaved local draft · not saved yet.</span>`}
      </div>
    <//>
  </div>`;
}
